import React, { useState } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { formatDate, formatNumber } from "utils";
import { useAuth, useLinks, useMission } from "hooks";
import { margins } from "style";
import { PrimaryLink } from "components/Links";
import ListTable from "components/ListTable";
import { EditSquadMissionForm } from "components/Squads";
import { EditButton } from "components/Buttons";
import { MissionLink } from "components/Missions";

/**
 * SquadDetails
 *
 * @param {Array}    data
 * @param {Boolean}  loading
 */
const SquadDetails = ({ data, loading, ...props }) => {
  const isMissionConnected = data?.missionId;
  const [editMissionForm, setEditMissionForm] = useState(false);
  const { data: missionData, loading: missionLoading } = useMission({ id: data?.missionId });
  const { isAdmin } = useAuth();
  const links = useLinks();
  const isLoading = loading || missionLoading;

  return (
    <>
      <ListTable
        title={<FormattedMessage id="Squads.Squad.Profile.ListTable.Title" />}
        secondaryTitle={
          isAdmin &&
          isMissionConnected && (
            <PrimaryLink withMoreIcon onClick={() => setEditMissionForm(true)}>
              <FormattedMessage id="Squads.Squad.Profile.ListTable.EditMission" />
            </PrimaryLink>
          )
        }
        marginSize={margins.normal}
        resultsPerPage={3} // resultsPerPage determines the number of loading row in the datatable.
        data={isLoading ? [] : ADDITIONAL_INFO(data, missionData, isAdmin, links.mission)}
        loading={isLoading}
        {...props}
      />
      {isAdmin && !isLoading && (
        <>
          {!isMissionConnected && (
            <EditButton data-header-edit-link onClick={() => setEditMissionForm(true)}>
              <FormattedMessage id="Squads.Squad.Profile.ListTable.LinkMission" />
            </EditButton>
          )}

          {editMissionForm && (
            <EditSquadMissionForm
              show={editMissionForm}
              onClose={() => setEditMissionForm(false)}
              mission={missionData}
              team={data}
            />
          )}
        </>
      )}
    </>
  );
};

const ADDITIONAL_INFO = (data, missionData, isAdmin, linkFunc) => {
  return [
    {
      label: <FormattedMessage id="Squads.Squad.Profile.ListTable.SquadName" />,
      value: data?.name,
    },
    ...(missionData?.name
      ? [
          {
            label: <FormattedMessage id="Squads.Squad.Profile.ListTable.Name" />,
            value: isAdmin ? (
              <MissionLink data={missionData} placement="auto" isEdit withOverlay />
            ) : (
              <PrimaryLink to={linkFunc?.(missionData?.uuid)} routerEnabled withMoreIcon>
                {missionData?.name}
              </PrimaryLink>
            ),
          },
        ]
      : []),
    {
      label: <FormattedMessage id="Squads.Squad.Profile.ListTable.Details.ActiveSince" />,
      value: data?.activeSince && formatDate(data?.activeSince),
    },
    {
      label: <FormattedMessage id="Squads.Squad.Profile.ListTable.Details.NumberOfDaysActive" />,
      value: data?.activeDays && `${formatNumber(data?.activeDays)} days`,
    },
    ...(missionData?.description
      ? [
          {
            label: <FormattedMessage id="Squads.Squad.Profile.ListTable.Details.description" />,
            description: missionData?.description,
          },
        ]
      : []),
  ];
};

SquadDetails.propTypes = {
  data: PropTypes.array,
  loading: PropTypes.bool,
};

export default SquadDetails;
